/**
 * Copyright SimVentions, Inc. Usage, distribution, transferal, and licensing
 * of this source code is protected under SBIR law as described in DFARS 252.227-7018.
 *
 * SBIR data rights fully described in the README.md file in the top level directory of this project.
 */
import React from "react";
import SyntaxHighlighter from "react-syntax-highlighter";
import {
  a11yDark,
  atomOneLight,
  solarizedLight,
} from "react-syntax-highlighter/dist/esm/styles/hljs";
import { useDexterTheme } from "../../DesignSystem/Theme/ThemeContext";
import { FileType } from "../../Api/Api";
import { ThemeNames } from "../../DesignSystem/Theme/Theme";

function getLanguage(fileType: FileType): string {
  switch (fileType) {
    case FileType.PROTO:
      return "protobuf";
    case FileType.XML:
      return "xml";
    default:
      return "plaintext";
  }
}

// Comment-token override colors — lifted to meet AA (>=4.5:1) on each node background.
// Base hljs comment tokens are tuned for each style's own bg, not for transparent
// rendering on a colored node card, so we override them per active dark theme.
// dark node           #3d4551: #a1b6c1 (4.60:1)
// colorblindDark node #1c3f5e: #85adc7 (4.58:1)
const DARK_STYLE_MAP: Record<string, [any, string]> = {
  // a11yDark worst-case token 4.87:1 on #3d4551; comment #a1b6c1 4.60:1 (both AA)
  operatorGray: [a11yDark, "#a1b6c1"],
  // a11yDark worst-case token 5.49:1 on #1c3f5e; comment #85adc7 4.58:1 (both AA)
  [ThemeNames.COLORBLIND_DARK]: [a11yDark, "#85adc7"],
};

/**
 * Overrides comment token color in a given hljs style to meet AA contrast on dark surfaces.
 *
 * Comment tokens are intentionally de-emphasized in every hljs style — the defaults are
 * tuned for the style's own background, not for transparent rendering on a colored node card.
 * This override lifts comments to a readable mid-tone without disrupting other token colors.
 */
function withReadableComments(style: any, commentColor: string): any {
  return {
    ...style,
    "hljs-comment": {
      ...(style["hljs-comment"] ?? {}),
      color: commentColor,
    },
    "hljs-quote": {
      ...(style["hljs-quote"] ?? {}),
      color: commentColor,
    },
  };
}

// Selects the dark-theme syntax palette. Only one non-colorblind dark palette
// currently exists (operatorGray → node #3d4551 in Theme.ts).
const ACTIVE_DARK_PALETTE: keyof typeof DARK_STYLE_MAP = "operatorGray";

export function NodeSyntaxHighlighter({
  code,
  fileType,
  wrapLines = false,
}: {
  code: string;
  fileType: FileType;
  wrapLines?: boolean;
}): JSX.Element {
  const { currentThemeName, currentTheme } = useDexterTheme();
  // Dark uses palette-specific style with comment contrast override
  let style: any;
  if (currentThemeName === ThemeNames.DARK) {
    const [baseStyle, commentColor] = DARK_STYLE_MAP[ACTIVE_DARK_PALETTE];
    style = withReadableComments(baseStyle, commentColor);
  } else if (currentThemeName === ThemeNames.COLORBLIND_DARK) {
    // ColorblindDark uses a11yDark with its own comment override
    const [baseStyle, commentColor] =
      DARK_STYLE_MAP[ThemeNames.COLORBLIND_DARK];
    style = withReadableComments(baseStyle, commentColor);
  } else if (currentThemeName === ThemeNames.COLORBLIND_LIGHT) {
    style = solarizedLight;
  } else {
    style = atomOneLight;
  }
  return (
    <SyntaxHighlighter
      language={getLanguage(fileType)}
      style={style}
      PreTag="div"
      wrapLines={wrapLines}
      customStyle={{
        margin: 0,
        padding: 0,
        background: "transparent",
        fontFamily: currentTheme.fonts.primary,
        fontSize: currentTheme.fontSizes.medium,
        color: currentTheme.colors.text,
        whiteSpace: "pre-wrap",
      }}
    >
      {code}
    </SyntaxHighlighter>
  );
}
